/**
 * Centralized SEO configuration for LexiQuest.
 *
 * NOTE: This is a client-side SPA with no URL-based routing (views are state-driven).
 * For full SEO effectiveness with search engines, consider adding a prerender service
 * (e.g., react-snap, prerender.io, or Rendertron) so crawlers receive fully rendered HTML.
 * Do NOT install one without explicit instruction — this note is for future reference.
 *
 * Production URL is read from VITE_SITE_URL env var (set in Railway/deployment).
 * Fallback: https://lexiquest.app
 */

const SITE_URL = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL)
  || 'https://lexiquest.app';

const DEFAULTS = {
  siteName: 'LexiQuest',
  siteUrl: SITE_URL,
  title: 'LexiQuest — Semantic Word Exploration Game',
  description:
    'Navigate between words through semantic connections in this free online word game. ' +
    'Similar to Wavelength and Infinite Craft — expand, combine, and discover word relationships. ' +
    'Play solo or multiplayer.',
  ogImage: `${SITE_URL}/og-image.png`,
  ogType: 'website',
  twitterCard: 'summary_large_image',
  locale: 'en_US',
  keywords:
    'word game, semantic game, word association, wavelength game, infinite craft, ' +
    'word exploration, vocabulary game, multiplayer word game, free online word game, lexiquest',
};

/**
 * Per-view SEO overrides. Keys match the `view` state in App.jsx.
 * Missing fields fall back to DEFAULTS.
 */
const VIEW_META = {
  landing: {
    title: 'LexiQuest — Free Semantic Word Game | Like Wavelength & Infinite Craft',
    description:
      'Play LexiQuest — a free word exploration game where you navigate semantic connections ' +
      'between words. Expand nodes, combine words, and find the target. ' +
      'Like Wavelength meets Infinite Craft. Solo or multiplayer.',
    canonicalPath: '/',
  },
  solo: {
    title: 'Solo Game — LexiQuest | Semantic Word Puzzle',
    description:
      'Challenge yourself in LexiQuest solo mode. Navigate a force-directed graph of ' +
      'semantically similar words from start to target. Beat par for a high score.',
    canonicalPath: '/',
  },
  lobby: {
    title: 'Multiplayer Lobby — LexiQuest',
    description:
      'Join a multiplayer LexiQuest room. Race friends to find the target word through ' +
      'semantic word connections. Up to 8 players.',
    canonicalPath: '/',
  },
  multiplayer: {
    title: 'Multiplayer Game — LexiQuest',
    description:
      'Multiplayer LexiQuest — race other players through a shared semantic word graph. ' +
      'Expand, combine, and bomb your way to the target word first.',
    canonicalPath: '/',
  },
};

export { DEFAULTS, VIEW_META, SITE_URL };
